/**
 * White-Label → Agents & visuals → Avatar templates.
 *
 * Edits `agentAvatars.textures`: per palette colour, the template images an agent
 * of that colour wears (uploaded like the logo — a data URL in the draft, saved
 * with the rest of the brand). Sets are kept in their stored order; a new set is
 * appended, because the order decides which set an agent with no set of its own
 * is hashed into (see `brand/agent-avatars.ts`), and re-sorting on every edit would
 * quietly re-dress those agents.
 *
 * The live strip renders real `BotAvatar`s under the DRAFT brand, so what it shows
 * is exactly what the app will draw after Save.
 */

import { useMemo, useState } from 'react';
import { Button, Upload, message as antdMessage } from 'antd';
import type { UploadProps } from 'antd';
import { BrandScope } from '@/brand';
import {
  AGENT_AVATAR_LIMITS,
  isSafeAvatarImage,
  normalizeHex,
} from '@/brand/agent-avatars';
import type { BrandAgentAvatarSet, BrandConfig } from '@/brand/types';
import { BotAvatar } from '@/components/common/BotAvatar';
import { useLanguage } from '@/hooks/useLanguage';

const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml';

interface Props {
  /** The draft palette (hex), in order. */
  palette: string[];
  /** The draft sets; undefined = the tenant never chose any. */
  value: BrandAgentAvatarSet[] | undefined;
  onChange: (sets: BrandAgentAvatarSet[]) => void;
  /** The whole draft brand, for the live strip. */
  previewBrand: BrandConfig;
}

function dataChars(sets: BrandAgentAvatarSet[]): number {
  return sets.reduce((n, s) => n + s.images.reduce((m, img) => m + (img.startsWith('data:') ? img.length : 0), 0), 0);
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function AgentAvatarSets({ palette, value, onChange, previewBrand }: Props) {
  const { t } = useLanguage();
  const [busyColor, setBusyColor] = useState<string | null>(null);
  const sets = useMemo(() => value ?? [], [value]);
  const paletteHex = useMemo(
    () => Array.from(new Set(palette.map(normalizeHex).filter((c): c is string => !!c))),
    [palette],
  );
  const setFor = (hex: string) => sets.find(s => normalizeHex(s.color) === hex);
  const orphans = sets.filter(s => !paletteHex.includes(normalizeHex(s.color) ?? ''));

  function replaceSet(hex: string, images: string[]) {
    const idx = sets.findIndex(s => normalizeHex(s.color) === hex);
    if (images.length === 0) {
      onChange(sets.filter((_, i) => i !== idx));
    } else if (idx >= 0) {
      onChange(sets.map((s, i) => (i === idx ? { color: hex, images } : s)));
    } else {
      onChange([...sets, { color: hex, images }]);
    }
  }

  async function addFiles(hex: string, files: File[]) {
    const existing = setFor(hex)?.images ?? [];
    if (!setFor(hex) && sets.length >= AGENT_AVATAR_LIMITS.maxSets) {
      antdMessage.error(t('whiteLabel.agentAvatarsTooManySets', { count: AGENT_AVATAR_LIMITS.maxSets }));
      return;
    }
    setBusyColor(hex);
    try {
      const added: string[] = [];
      for (const file of files) {
        if (existing.length + added.length >= AGENT_AVATAR_LIMITS.maxImagesPerSet) {
          antdMessage.error(t('whiteLabel.agentAvatarsSetFull', { count: AGENT_AVATAR_LIMITS.maxImagesPerSet }));
          break;
        }
        const dataUrl = await readAsDataUrl(file);
        if (!isSafeAvatarImage(dataUrl)) {
          antdMessage.error(t('whiteLabel.agentAvatarsUnsupported', { name: file.name }));
          continue;
        }
        if (dataUrl.length > AGENT_AVATAR_LIMITS.maxImageChars) {
          antdMessage.error(t('whiteLabel.agentAvatarsTooLarge', { name: file.name }));
          continue;
        }
        const next = sets.filter(s => normalizeHex(s.color) !== hex);
        if (dataChars(next) + dataChars([{ color: hex, images: [...existing, ...added, dataUrl] }])
            > AGENT_AVATAR_LIMITS.maxTotalChars) {
          antdMessage.error(t('whiteLabel.agentAvatarsBudget'));
          break;
        }
        added.push(dataUrl);
      }
      if (added.length > 0) replaceSet(hex, [...existing, ...added]);
    } finally {
      setBusyColor(null);
    }
  }

  const uploadProps = (hex: string): UploadProps => ({
    accept: ACCEPT,
    multiple: true,
    showUploadList: false,
    // antd calls this once per file; collect the batch from the file list.
    beforeUpload: (file, fileList) => {
      if (file === fileList[0]) void addFiles(hex, fileList as unknown as File[]);
      return false;
    },
  });

  const row = (hex: string, set: BrandAgentAvatarSet | undefined, inPalette: boolean) => (
    <div key={hex} className="wl-avatar-set" data-testid={`wl-avatar-set-${hex.slice(1)}`}>
      <span className="wl-avatar-set__swatch" style={{ background: hex }} aria-hidden />
      <div className="wl-avatar-set__body">
        <div className="wl-avatar-set__head">
          <span className="wl-avatar-set__hex" dir="ltr">{hex}</span>
          {!inPalette && <span className="wl-avatar-set__note">{t('whiteLabel.agentAvatarsNotInPalette')}</span>}
        </div>
        <div className="wl-avatar-set__images">
          {(set?.images ?? []).map((img, i) => (
            <div key={i} className="wl-avatar-set__tile" style={{ backgroundImage: `url(${JSON.stringify(img)})` }}>
              <button
                type="button"
                className="wl-avatar-set__remove"
                onClick={() => replaceSet(hex, (set?.images ?? []).filter((_, j) => j !== i))}
                title={t('whiteLabel.agentAvatarsRemoveImage')}
                aria-label={t('whiteLabel.agentAvatarsRemoveImage')}
              >
                ×
              </button>
            </div>
          ))}
          {inPalette && (
            <Upload {...uploadProps(hex)}>
              <Button size="small" loading={busyColor === hex} data-testid={`wl-avatar-upload-${hex.slice(1)}`}>
                {t('whiteLabel.agentAvatarsUpload')}
              </Button>
            </Upload>
          )}
          {set && (
            <Button size="small" type="text" danger onClick={() => replaceSet(hex, [])}>
              {t('whiteLabel.agentAvatarsRemoveSet')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="wl-avatar-sets">
      {paletteHex.map(hex => row(hex, setFor(hex), true))}
      {orphans.map(s => row(normalizeHex(s.color) ?? s.color, s, false))}

      <div className="wl-avatar-sets__preview" aria-label={t('whiteLabel.agentAvatarsPreview')}>
        <span className="wl-avatar-sets__preview-label">{t('whiteLabel.agentAvatarsPreview')}</span>
        <BrandScope brand={previewBrand}>
          {paletteHex.map(hex => (
            <BotAvatar key={hex} name={`preview-${hex}`} title={hex} size={40} colorOverride={hex} variantOverride={0} />
          ))}
          <BotAvatar name={t('whiteLabel.agentAvatarsNoColor')} size={40} />
        </BrandScope>
      </div>

      {sets.length === 0 ? (
        <div className="wl-avatar-sets__empty">{t('whiteLabel.agentAvatarsNone')}</div>
      ) : (
        <Button size="small" danger onClick={() => onChange([])} data-testid="wl-avatar-clear">
          {t('whiteLabel.agentAvatarsClear')}
        </Button>
      )}
    </div>
  );
}
